import Image from "next/image";
import { TemporaryAccessTokenCreateRequestBody } from "@gocardless/api/dashboard/types";
import { useLingui } from "@lingui/react";
import { t } from "@lingui/macro";
import { Box } from "@gocardless/flux-react";

import weakPasswordBannerImage from "../../../assets/svg/weak-password-banner-image.svg";
import {
  SignInApiErrors,
  PasswordResetDelayNotApplicableErrors,
} from "../SignIn/useSignIn";
import { ForcedPasswordResetMetaData } from "../SignIn/types";

import ForcedPasswordResetCard from "./PasswordResetCards/ForcedPasswordResetCard";
import { ForcedPasswordResetMessage } from "./ForcedPasswordResetMessage";
import { PasswordResetBanner, PasswordResetTitle } from "./PasswordResetCopy";

import { SingleFixedLayout } from "src/components/layout/core/SingleFixedLayout";
import { LogoHeader } from "src/components/layout/core/Headers/LogoHeader";
import { HelpButton } from "src/components/layout/core/Headers/Buttons/HelpButton";

interface ForcedPasswordResetProps {
  forcedPasswordResetMetaData: ForcedPasswordResetMetaData;
  submit: (requestBody: TemporaryAccessTokenCreateRequestBody) => void;
  handleSignOut?: () => void;
}

export const ForcedPasswordReset = ({
  forcedPasswordResetMetaData,
  submit,
  handleSignOut,
}: ForcedPasswordResetProps) => {
  const { i18n } = useLingui();
  const altText = i18n._(
    t({
      id: "forced-password-reset.image.alt-text",
      message:
        "illustration of a woman sitting on a stool, with shapes flying past",
    })
  );

  const {
    error_reason: errorType,
    days_until_required_password_reset: daysUntilRequiredPasswordReset,
    user_email: userEmail,
  } = forcedPasswordResetMetaData;

  const showGoToDashboardButton = !!(
    errorType === SignInApiErrors.RequirePasswordResetForPasswordPolicy
  );

  const showSignOutButton =
    !!PasswordResetDelayNotApplicableErrors.includes(errorType);

  return (
    <SingleFixedLayout
      header={{
        content: <LogoHeader rightContent={<HelpButton />} />,
      }}
    >
      <Box data-testid="forced-password-reset">
        <ForcedPasswordResetCard
          banner={
            <PasswordResetBanner
              errorType={errorType}
              daysUntilRequiredPasswordReset={daysUntilRequiredPasswordReset}
            />
          }
          title={<PasswordResetTitle errorType={errorType} />}
          description={
            <ForcedPasswordResetMessage
              forcedPasswordResetMetaData={forcedPasswordResetMetaData}
              daysUntilRequiredPasswordReset={daysUntilRequiredPasswordReset}
            />
          }
          showGoToDashboardButton={showGoToDashboardButton}
          showSignOutButton={showSignOutButton}
          userEmail={userEmail}
          image={
            <Image
              src={weakPasswordBannerImage}
              alt={altText}
              height={223}
              width={273}
            />
          }
          submit={submit}
          handleSignOut={handleSignOut}
        />
      </Box>
    </SingleFixedLayout>
  );
};

export default ForcedPasswordReset;
